import React from 'react'
import * as RadixToast from '@radix-ui/react-toast'
import { IconTimes } from './../icons/library'
import { Button, ButtonShape, ButtonStyle } from './../components/simple/button'
import { TruncatedText } from '../components/simple/truncatedText'

export interface Toast {
  subject?: React.ReactNode
  message: React.ReactNode
  duration?: number
  error?: boolean
  closeOnClick?: boolean
}

interface ToastContext {
  showToast: (toast: Toast) => void
}

export const toastContext = React.createContext<ToastContext>({} as ToastContext)

/**
 * A hook to get the Toast context, allowing you to show toasts.
 * In most cases, you can just use the Toasts object directly, since we usually do not need to react to the toast state.
 */
export const useToast = () => {
  return React.useContext(toastContext)
}

/**
 * A singleton object that allows toasts to be made from outside of react.
 * This is safe to use if you do not need to react to the toast state. Otherwise, use useToast() instead.
 */
export const Toasts = {
  showToast: (toast: Toast) => {},
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<(Toast & { id: number })[]>([])
  const idRef = React.useRef(0)

  const showToast = React.useCallback((toast: Toast) => {
    const id = idRef.current++
    setToasts((prevToasts) => [...prevToasts, { id, ...toast }])
  }, [])

  // Setup the singleton that allows toasts to be made from outside of react
  React.useEffect(() => {
    Toasts.showToast = showToast
  }, [showToast])

  const removeToast = React.useCallback((id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id))
  }, [])

  const shadow =
    'data-[state=open]:shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px]'

  return (
    <RadixToast.Provider>
      <toastContext.Provider value={{ showToast }}>
        {children}
        {toasts.map((toast) => (
          <RadixToast.Root
            key={toast.id}
            open={true}
            onOpenChange={() => setTimeout(() => removeToast(toast.id), 0)}
            duration={toast.duration || 5000}
            onSwipeStart={(event) => event.preventDefault()}
            onSwipeEnd={(event) => event.preventDefault()}
            onSwipeMove={(event) => event.preventDefault()}
            className={`pointer-events-auto flex max-h-[90vh] max-w-80 justify-between overflow-auto ${!toast.subject ? 'items-center' : ''} rounded-lg ${toast.error ? 'bg-red-500' : 'bg-blue-500'} p-4 text-sm text-white [&_a]:text-white ${shadow}`}
            onClick={() => toast.closeOnClick && removeToast(toast.id)}
          >
            <div className="flex select-text flex-col">
              {toast.subject && <RadixToast.Title className="font-bold">{toast.subject}</RadixToast.Title>}
              <RadixToast.Description>
                <TruncatedText lines={10}>{toast.message}</TruncatedText>
              </RadixToast.Description>
            </div>
            <RadixToast.Close asChild>
              <Button
                buttonStyle={ButtonStyle.toastClose}
                shape={ButtonShape.close}
                className="pointer-events-auto -mr-2 -mt-2 ml-3 self-start text-white"
              >
                <IconTimes thicken={10} />
              </Button>
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="fixed bottom-0 right-0 z-[9999999] flex flex-col-reverse gap-3 p-5" />
      </toastContext.Provider>
    </RadixToast.Provider>
  )
}
